import type { PlanetType } from "../entities";
import { type MapDef, MapFormatError } from "./map-def";

/** legacy: Planet.h `enum planet_t { MARS=1, EARTH=2, PLUTO=3 }` */
const LEGACY_PLANET_TYPES: Record<number, PlanetType> = { 1: "mars", 2: "earth", 3: "pluto" };

/**
 * Parses the original plain-text `.map` format (WorldMapRegistry.cpp):
 *
 *     <width> <height>
 *     PLANET <mass> <radius> <type> <x> <y>
 *     SPAWNPOINT <x> <y>
 *     FUEL <x> <y> <amount>
 *
 * Legacy coordinates are y-up; they are flipped (y' = height - y) into sim space.
 */
export function parseLegacyMap(text: string, name: string): MapDef {
	const lines = text
		.split(/\r?\n/)
		.map((line) => line.trim())
		.filter((line) => line.length > 0);

	const header = lines.shift();
	if (header === undefined) throw new MapFormatError(`${name}: empty map file`);
	const [w, h] = numbers(header.split(/\s+/), 2, `${name}: header`);
	if (!(w > 0 && h > 0)) throw new MapFormatError(`${name}: world size must be positive`);

	const map: MapDef = { name, size: { w, h }, planets: [], spawnPoints: [], fuelItems: [] };

	lines.forEach((line, i) => {
		const [keyword, ...args] = line.split(/\s+/);
		const where = `${name}: line ${i + 2} (${keyword})`;
		switch (keyword) {
			case "PLANET": {
				const [mass, radius, typeId, x, y] = numbers(args, 5, where);
				const type = LEGACY_PLANET_TYPES[typeId];
				if (!type) throw new MapFormatError(`${where}: unknown planet type ${typeId}`);
				map.planets.push({ type, mass, radius, x, y: h - y });
				break;
			}
			case "SPAWNPOINT": {
				const [x, y] = numbers(args, 2, where);
				map.spawnPoints.push({ x, y: h - y });
				break;
			}
			case "FUEL": {
				const [x, y, amount] = numbers(args, 3, where);
				map.fuelItems.push({ x, y: h - y, amount });
				break;
			}
			default:
				throw new MapFormatError(`${where}: unknown keyword`);
		}
	});

	return map;
}

function numbers(tokens: string[], count: 2, where: string): [number, number];
function numbers(tokens: string[], count: 3, where: string): [number, number, number];
function numbers(
	tokens: string[],
	count: 5,
	where: string,
): [number, number, number, number, number];
function numbers(tokens: string[], count: number, where: string): number[] {
	if (tokens.length < count) {
		throw new MapFormatError(`${where}: expected ${count} values, got ${tokens.length}`);
	}
	const values = tokens.slice(0, count).map(Number);
	if (values.some((v) => !Number.isFinite(v))) {
		throw new MapFormatError(`${where}: invalid number in "${tokens.join(" ")}"`);
	}
	return values;
}
